/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { cloneDeep, isEqualWith } from "lodash";
import React, { useEffect, useState } from "react";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { IntegrationAPI } from "../../../../api/IntegrationAPI";
import { CampaignActionAtom } from "../../../../atoms/CampaignActionAtom";
import {
  ActiveCampaignAtom,
  TempCampaignAtom,
} from "../../../../atoms/CampaignAtom";
import { CampaignEmailAtom } from "../../../../atoms/CampaignEmailAtom";
import { CampaignUnsavedChangesAtom } from "../../../../atoms/CampaignUnsavedChangesAtom";
import {
  CampaignScreen,
  CampaingBuilderFormAtom,
} from "../../../../atoms/CampaingBuilderFormAtom";
import { NodeIndexAtom } from "../../../../atoms/NodeIndexAtom";
import { TagsAtom } from "../../../../atoms/TagsAtom";
import { UserAtom } from "../../../../atoms/UserAtom";
import LearnisticTag from "../../../../components/LearnisticTag";
import { ILearnisticApp } from "../../../../generated/Api";
import { Campaign, INode, INodeTypes } from "../../../../types/CampaignTypes";
import { LearnisticAppsToTags } from "../../../../utils/LearnisticTagConvertions";
import { CUSTOM_TAG } from "../../../Contacts/Header";
import IconBerserkerMail from "../../../Icon";
import { AllIntegrationsAtom } from "../../../Settings/Integrations/Integrations";
import Tag from "../../../Tag";
import { ChangeAtom } from "../Form/Email";
import Actions from "./Actions";
import DelayCard from "./DelayCard";
import InitialCard from "./InitialCard";
import NodeCard from "./NodeCard";
import StopCard from "./StopCard";

const Container = styled.div`
  width: 100%;
  background-color: #f9f9f9;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  overflow-y: scroll;
`;

const LineDashed = styled.div`
  width: 1px;
  min-height: 30px;
  border: 1px dashed #bbbbbb;
`;

const Empty = styled.div`
  min-width: 32px;
  min-height: 32px;
  border-radius: 50%;
`;

const Starter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 70%;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #2f2f2f;
  padding: 10px 0px;
`;

const SubTitle = styled.div`
  font-size: 16px;
  color: #707070;
  width: 400px;
  text-align: center;
  line-height: 1.4;
`;

const Tags = styled.div`
  margin-top: 10px;

  display: flex;
  align-items: center;
  justify-content: start;
  flex-wrap: wrap;
  grid-gap: 12px;

  #tag-container {
    max-width: 100%;
  }

  div {
    margin: 0;
  }
`;

type ActionButtonsType = {
  icon: JSX.Element;
  backgroundColor: string;
  tooltipLabel: string;
  screen: CampaignScreen;
  type: INodeTypes;
};

const typeScreenMap: { [key: string]: CampaignScreen } = {
  [INodeTypes.ManageTag]: CampaignScreen.TAG,
  [INodeTypes.StopTag]: CampaignScreen.TAG,
  [INodeTypes.Email]: CampaignScreen.EMAIL,
  [INodeTypes.WebHook]: CampaignScreen.WEBHOOK,
  [INodeTypes.PushNotification]: CampaignScreen.PUSH_NOTIFICATION,
  [INodeTypes.Sms]: CampaignScreen.SMS_NOTIFICATION,
};

interface Props {
  erroredCards: number[];
}

export default function Builder({ erroredCards }: Props): JSX.Element {
  // const setUnsavedChanges = useSetRecoilState(CampaignUnsavedChangesAtom);
  const setUnsavedChanges = useSetRecoilState(CampaignUnsavedChangesAtom);
  const [isActiveButtonWrapper, setIsActiveButtonWrapper] = useState(-1);
  const setForm = useSetRecoilState(CampaingBuilderFormAtom);
  const [nodeIndex, setNodeIndex] = useRecoilState(NodeIndexAtom);
  const [activeCampaign, setActiveCampaign] =
    useRecoilState(ActiveCampaignAtom);
  const action = useRecoilValue(CampaignActionAtom);
  const [tempCampaign, setTempCampaign] = useRecoilState<Campaign | null>(
    TempCampaignAtom
  );
  const setHasChange = useSetRecoilState(ChangeAtom);
  const resetEmailCampaign = useResetRecoilState(CampaignEmailAtom);
  const tagsPool = useRecoilValue(TagsAtom);

  const setIntegrations = useSetRecoilState(AllIntegrationsAtom);
  const user = useRecoilValue(UserAtom);

  useEffect(() => {
    if (action === "edit") return;

    let tempCampaignClone = cloneDeep(tempCampaign);
    let activeCampaignClone = cloneDeep(activeCampaign);

    tempCampaignClone?.nodes.forEach((node) => {
      if (node.type === INodeTypes.DelayTimer && node.delayTimer.constraints) {
        node.delayTimer.constraints.time &&= dayjs(
          node.delayTimer.constraints.time
        ).format("HH:mm:ss");
      }
    });

    activeCampaignClone?.nodes.forEach((node) => {
      if (node.type === INodeTypes.DelayTimer && node.delayTimer.constraints) {
        node.delayTimer.constraints.time &&= dayjs(
          node.delayTimer.constraints.time
        ).format("HH:mm:ss");
      }
    });

    if (isEqualWith(tempCampaignClone, activeCampaignClone)) {
      setUnsavedChanges(false);
    } else {
      setUnsavedChanges(true);
    }
  }, [tempCampaign, activeCampaign, action]);

  useEffect(() => {
    (async () => {
      if (!user) return;
      let integrations = await IntegrationAPI.getAll();
      if (integrations) setIntegrations(integrations);
    })();
  }, [user]);

  useEffect(() => {
    //If we start create a campaign we start with tag screen
    if (
      (activeCampaign && activeCampaign.tagConditions?.length < 1) ||
      nodeIndex === -1
    ) {
      setForm(CampaignScreen.TAG);
    }
  }, []);

  useEffect(() => {
    setTempCampaign(activeCampaign);
  }, [activeCampaign]);

  function handleNodeClick(
    type: INodeTypes,
    screen: CampaignScreen,
    index: number
  ) {
    setForm(screen);
    setNodeIndex(index);
  }

  async function handleNodeDelete(index: number) {
    if (!tempCampaign || !activeCampaign) return;

    if (index === -2) {
      let newCampaignCopy: Campaign = {
        ...tempCampaign,
      };

      const { stopTagConditions, ...newCampaignCopyRest } = newCampaignCopy;

      const emptyCampagin =
        tempCampaign?.tagConditions?.length === 0 &&
        tempCampaign?.nodes.length === 0;

      setTempCampaign(newCampaignCopyRest);
      setForm(emptyCampagin ? CampaignScreen.TAG : null);
      setNodeIndex(-1);

      return;
    }

    const nodesArray = tempCampaign.nodes;

    const updatedNodesArray = [...nodesArray];

    if (updatedNodesArray[index - 1]?.type === INodeTypes.DelayTimer) {
      updatedNodesArray.splice(index - 1, 2);
    } else {
      updatedNodesArray.splice(index, 1);
    }

    const newCampaignCopy: Campaign = {
      ...tempCampaign,
      nodes: [...updatedNodesArray],
    };

    const emptyCampagin =
      tempCampaign?.tagConditions?.length === 0 &&
      tempCampaign?.stopTagConditions === undefined &&
      tempCampaign?.nodes.length === 1;

    setTempCampaign(newCampaignCopy);
    setForm(emptyCampagin ? CampaignScreen.TAG : null);
    setNodeIndex(-1);
  }

  function handleActionButtonClick(item: ActionButtonsType, index: number) {
    if (!tempCampaign || !activeCampaign) return;

    if (item.type === INodeTypes.Email) {
      resetEmailCampaign();
      setHasChange((c) => !c);
    }

    let newCampaign: Campaign = null;

    if (item.type === INodeTypes.StopTag) {
      newCampaign = {
        ...tempCampaign,
        stopTagConditions: [],
      };
      setNodeIndex(-2);
    } else {
      let nodesCopy = [...tempCampaign.nodes];

      let indexAux =
        index > 0 && nodesCopy[index - 1].type === INodeTypes.DelayTimer
          ? index - 1
          : index;

      nodesCopy.splice(indexAux, 0, {
        _id: "",
        name: "",
        type: item.type,
        //@ts-ignore
        ...(item.type === INodeTypes.Email && {
          //@ts-ignore
          email: {
            body: "",
            subject: "",
            footerId: tempCampaign.campaignOptions.defaultFooterId,
            aliasId: tempCampaign.campaignOptions.defaultAliasId,
            interestingURLs: {},
          },
        }),
      });

      newCampaign = {
        ...tempCampaign,
        nodes: [...nodesCopy],
      };

      setNodeIndex(indexAux);
    }

    setTempCampaign(newCampaign);
    setForm(item.screen);
  }

  async function cloneItem(indexToClone: number, type: "bottom" | "next") {
    if (!activeCampaign || !tempCampaign) return;

    // Clone the index
    const clonedItem = tempCampaign.nodes[indexToClone];
    const newCampaign = { ...tempCampaign };

    const delayIndex = indexToClone - 1;
    const delayNode = tempCampaign.nodes[delayIndex];
    const hasDelay = delayNode?.type === INodeTypes.DelayTimer;

    if (!clonedItem) {
      console.log("Error clonning this item");
      return;
    }

    if (type === "bottom") {
      newCampaign.nodes = [...newCampaign.nodes];

      if (hasDelay) {
        newCampaign.nodes.push({ ...delayNode, _id: undefined });
      }

      newCampaign.nodes.push({ ...clonedItem, _id: undefined });
    } else {
      const subsequentNodes = newCampaign.nodes.slice(indexToClone);

      newCampaign.nodes = [
        ...newCampaign.nodes.slice(0, hasDelay ? delayIndex : indexToClone),
      ];

      if (hasDelay) {
        newCampaign.nodes.push({ ...delayNode, _id: undefined });
      }

      newCampaign.nodes.push({ ...clonedItem, _id: undefined });
      newCampaign.nodes.push(...subsequentNodes);
    }

    setTempCampaign(newCampaign);
  }

  async function onInitialClick() {
    if (!activeCampaign || !tempCampaign) return;
    setForm(CampaignScreen.TAG);
    setNodeIndex(-1);
  }

  function hasDelay(index: number, error: boolean, type: INodeTypes) {
    const delayNode = tempCampaign ? tempCampaign!.nodes[index - 1] : undefined;
    if (index > 0 && delayNode && delayNode.type === INodeTypes.DelayTimer) {
      return (
        <>
          <DelayCard
            onClick={() => {
              setForm(typeScreenMap[tempCampaign.nodes[index]?.type]);
              setNodeIndex(index);
            }}
            unit={delayNode.delayTimer?.unit || 1}
            time={delayNode.delayTimer?.delay || 0}
            constraints={delayNode.delayTimer?.constraints}
            error={error}
            type={type}
          />
          <LineDashed />
        </>
      );
    }
  }

  function description(node: INode) {
    if (node.type === INodeTypes.Email) return node.email?.subject || "";
    if (
      (node.type === INodeTypes.ManageTag && node.manageTag) ||
      (node.type === INodeTypes.StopTag && node.stopTag)
    ) {
      let allTags = [];
      if (node.type === INodeTypes.ManageTag && node.manageTag) {
        allTags = [
          ...(
            (node.manageTag?.addedTags || []).map((t) => {
              return tagsPool.find((tg) => tg._id === t._id)!;
            }) || []
          ).filter((n) => n),
          ...(
            (node.manageTag?.removedTags || []).map((t) => {
              return tagsPool.find((tg) => tg._id === t._id)!;
            }) || []
          ).filter((n) => n),
          ...LearnisticAppsToTags(node.manageTag?.addedLearnisticTags || []),
          ...LearnisticAppsToTags(node.manageTag?.removedLearnisticTags || []),
        ];
      }
      if (node.type === INodeTypes.StopTag && node.stopTag) {
        allTags = [
          ...tagsPool.filter((t) =>
            (tempCampaign.stopTagConditions || []).find((n) => n === t._id)
          ),
          ...LearnisticAppsToTags(
            ((tempCampaign.stopTagConditions || []).filter(
              (n) => n && typeof n !== "string"
            ) as ILearnisticApp[]) || []
          ),
        ];
      }
      if (allTags.length < 3) {
        return (
          <Tags>
            {allTags.map((elem) => {
              return elem?._id ? (
                <Tag key={`flex_tags_any_${elem._id}`} tag={elem as any} />
              ) : (
                <LearnisticTag
                  key={`recipient_tag_tags_builder_${elem?.id}`}
                  tag={elem}
                />
              );
            })}
          </Tags>
        );
      } else {
        const [explicitTag, explicitTag2, ...bundledTags] = allTags;

        const explicitTags = [explicitTag, explicitTag2];
        const system = !bundledTags.find(
          (bTag) => bTag?.type === "user" || bTag?.id
        );

        return (
          <Tags>
            {explicitTags.map((et) =>
              et?._id ? (
                <Tag key={`flex_tags_any_${et._id}`} tag={et as any} />
              ) : (
                <LearnisticTag
                  key={`recipient_tag_tags_builder_${et?.id}`}
                  tag={et}
                />
              )
            )}
            <div data-tip data-for="tag">
              <Tag
                tag={CUSTOM_TAG(allTags.length - 2, system)}
                tooltip={bundledTags
                  .map(
                    // If the tag doesn't have a _id, then it's from Learnistic
                    ({ category, name, _id: berserkerMailId }) =>
                      `${berserkerMailId ? category : "LEARNISTIC"} -> ${name}`
                  )
                  .join("\n")}
              />
            </div>
          </Tags>
        );
      }
    }
    return node.name || "";
  }

  if (!tempCampaign || !activeCampaign) return <div />;

  return (
    <Container>
      {tempCampaign?.tagConditions?.length === 0 &&
      tempCampaign?.stopTagConditions === undefined &&
      tempCampaign?.nodes.length === 0 ? (
        <Starter>
          <IconBerserkerMail name="start-campaign" />
          <Title>Start building your campaign</Title>
          <SubTitle>
            Select at least one tag that will be used
            <br />
            to automatically start this campaign
          </SubTitle>
        </Starter>
      ) : (
        <>
          <InitialCard
            onClick={onInitialClick}
            selected={nodeIndex === -1}
            error={erroredCards.includes(-1)}
          />

          {tempCampaign.nodes.map((n, index) => {
            if (n.type === INodeTypes.DelayTimer) return;
            return (
              <>
                {(action === "edit" ||
                  (action === "create" &&
                    !n._id &&
                    !activeCampaign.campaignOptions.fixed)) &&
                  (nodeIndex === index ||
                    nodeIndex + 1 === index ||
                    (nodeIndex + 2 === index &&
                      tempCampaign.nodes[nodeIndex + 1]?.type ===
                        INodeTypes.DelayTimer)) && (
                    <>
                      <LineDashed />
                      <Actions
                        isAtived
                        isActiveButtonWrapper={isActiveButtonWrapper}
                        handleIsActiveButtonWrapper={setIsActiveButtonWrapper}
                        handleActionButtonClick={handleActionButtonClick}
                        numberActived={index}
                        noStopTag
                      />
                      {isActiveButtonWrapper === index && <Empty />}
                    </>
                  )}
                <LineDashed />
                {(n.type === INodeTypes.Email ||
                  n.type === INodeTypes.WebHook ||
                  n.type === INodeTypes.ManageTag ||
                  n.type === INodeTypes.StopTag) &&
                  hasDelay(index, erroredCards.includes(index - 1), n.type)}
                <NodeCard
                  clone={(type) => cloneItem(index, type)}
                  original={!!n._id}
                  content={n}
                  error={erroredCards.includes(index)}
                  selected={nodeIndex === index}
                  description={description(n)}
                  type={n.type}
                  onClick={() =>
                    handleNodeClick(n.type, typeScreenMap[n.type], index)
                  }
                  onDelete={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNodeDelete(index);
                  }}
                  noDelete={!!activeCampaign.campaignOptions.fixed}
                  shouldCloneToEnd={
                    tempCampaign.nodes.length - 1 === index ||
                    (action === "create" &&
                      !!n._id &&
                      (activeCampaign.nodes.length - 1 !== index ||
                        activeCampaign.nodes.length ===
                          tempCampaign.nodes.length))
                  }
                />
              </>
            );
          })}
          {!activeCampaign.campaignOptions.fixed && (
            <>
              <LineDashed />
              <Actions
                isAtived
                isActiveButtonWrapper={isActiveButtonWrapper}
                handleIsActiveButtonWrapper={setIsActiveButtonWrapper}
                handleActionButtonClick={handleActionButtonClick}
                numberActived={tempCampaign.nodes.length}
                noStopTag={tempCampaign.stopTagConditions !== undefined}
              />
              {isActiveButtonWrapper === tempCampaign.nodes.length && <Empty />}
            </>
          )}
          {!!Array.isArray(tempCampaign.stopTagConditions) && (
            <>
              <LineDashed />
              <StopCard
                original={true}
                error={erroredCards.includes(-2)}
                selected={nodeIndex === -2}
                description={description({
                  type: INodeTypes.StopTag,
                  stopTag: { tags: [] },
                } as INode)}
                onClick={() =>
                  handleNodeClick(
                    INodeTypes.StopTag,
                    typeScreenMap[INodeTypes.StopTag],
                    -2
                  )
                }
                onDelete={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNodeDelete(-2);
                }}
              />
            </>
          )}
        </>
      )}
    </Container>
  );
}
